import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, BarChart3, Bot, Globe2, MessageSquare, ShieldCheck, Sparkles, Workflow, CheckCircle2, LineChart, Zap, Languages } from "lucide-react";

export default function HomePage() {
  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="brand-radial absolute inset-0 -z-10" />
        <div className="brand-grid absolute inset-0 -z-10 opacity-40" />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 slide-up">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-[#1F3A6B] bg-[#FFF0DF] border border-[#FFE1BE] rounded-full px-3 py-1">
                <Sparkles size={14} className="text-[#F58220]" /> AI-first CX & NPS Intelligence
              </span>
              <h1 className="mt-5 text-4xl md:text-6xl font-black tracking-tight text-[#1F3A6B] leading-[1.05]">
                Measure loyalty.<br/>
                <span className="brand-gradient-text">Predict churn.</span><br/>
                Act in real time.
              </h1>
              <p className="mt-5 text-lg text-slate-600 max-w-xl">
                CX10X is the delightful, AI-first Customer Experience platform. Capture feedback across every touch point, get boardroom-grade insight in real time — in any language.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/signup">
                  <button data-testid="hero-cta-start" className="btn-pill text-[15px] px-7 py-3.5">
                    Start free — no card <ArrowRight className="ml-1.5" size={18} />
                  </button>
                </Link>
                <Link to="/calculator">
                  <button data-testid="hero-cta-calc" className="btn-pill ghost text-[15px] px-7 py-3.5">
                    Try NPS Calculator
                  </button>
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={16} className="text-emerald-500" /> 25+ question types</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={16} className="text-emerald-500" /> 90+ languages</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={16} className="text-emerald-500" /> Enterprise-grade security</span>
              </div>
            </div>
            <div className="lg:col-span-5 fade-in relative">
              <img src="/brand/mascot-builder.jpg" alt="" className="absolute -bottom-8 -right-4 w-36 h-36 object-contain mix-blend-multiply hidden lg:block pointer-events-none z-0"/>
              <HeroDashboardPreview />
            </div>
          </div>
        </div>
      </section>

      {/* LOGO STRIP */}
      <section className="border-y border-slate-100 bg-white/70">
        <div className="max-w-7xl mx-auto px-6 py-7 overflow-hidden">
          <p className="text-center text-xs uppercase tracking-widest text-slate-500">Trusted by modern CX teams</p>
          <div className="mt-4 overflow-hidden no-scrollbar">
            <div className="marquee">
              {["Aurora Retail","Helix Health","Northwind Bank","Lumen SaaS","Pacifico Hotels","Vanta Mobility","Atlas Telecom","Ember Studios","Aurora Retail","Helix Health","Northwind Bank","Lumen SaaS"].map((n, i) => (
                <div key={i} className="text-slate-400 font-semibold text-lg tracking-tight whitespace-nowrap">{n}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#F58220]">Platform</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-[#1F3A6B] tracking-tight">An end-to-end CX system, not a survey tool.</h2>
          <p className="mt-3 text-slate-600 text-lg">From listening to action — CX10X unifies surveys, journeys, analytics, and AI insights into a single intelligent workspace.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          <FeatureCard icon={<BarChart3 />} title="Advanced NPS Analytics" desc="Real-time NPS score, promoters/passives/detractors, segmentation, trends, and predictive churn signals."/>
          <FeatureCard icon={<Bot />} title="AI Conversational Analytics" desc="Ask in plain English: 'Why did NPS drop last week?' Get root-cause analysis, themes, and actions."/>
          <FeatureCard icon={<Workflow />} title="Journey Builder" desc="Event → Delay → Action → Condition. Trigger surveys at the perfect moment in any customer journey."/>
          <FeatureCard icon={<Languages />} title="90+ Languages" desc="Auto-detect, RTL support, and AI translation. Run global campaigns from a single workspace."/>
          <FeatureCard icon={<MessageSquare />} title="Closed-Loop Feedback" desc="Detractors auto-route to the right team with SLAs, escalation, and resolution tracking."/>
          <FeatureCard icon={<ShieldCheck />} title="Enterprise-grade Security" desc="Multi-tenant isolation, role-based access, brute-force protection, and audit-ready logs."/>
        </div>
      </section>

      {/* AI INSIGHTS BANNER */}
      <section className="bg-[#F58220] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-wider text-white/70 font-semibold">AI Insights</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight">Stop reading dashboards.<br/>Start having conversations with your data.</h2>
            <p className="mt-4 text-slate-200 text-lg max-w-xl">Our embedded analyst answers questions, surfaces themes, and recommends fixes — across thousands of responses, in any language.</p>
            <ul className="mt-6 space-y-3 text-slate-200">
              <li className="flex gap-3"><Zap className="text-white/70 shrink-0" /> &quot;Why is NPS dropping in Mumbai this week?&quot;</li>
              <li className="flex gap-3"><Zap className="text-white/70 shrink-0" /> &quot;Top 5 themes from detractors in last 30 days&quot;</li>
              <li className="flex gap-3"><Zap className="text-white/70 shrink-0" /> &quot;Compare onboarding NPS by plan&quot;</li>
            </ul>
          </div>
          <div className="glass-card rounded-2xl p-5 border border-white/10 bg-white/5">
            <FakeChat />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <Card className="overflow-hidden border-0 rounded-3xl" style={{ boxShadow: "var(--shadow-pop)" }}>
          <div className="grid md:grid-cols-2">
            <div className="p-10 md:p-14">
              <h3 className="text-3xl font-black text-[#1F3A6B] tracking-tight">Launch your first survey in 60 seconds.</h3>
              <p className="mt-3 text-slate-600">Free forever for small teams. No credit card. Cancel anytime.</p>
              <div className="mt-6 flex gap-3">
                <Link to="/signup"><button data-testid="bottom-cta-start" className="btn-pill">Create free account</button></Link>
                <Link to="/templates"><button data-testid="bottom-cta-templates" className="btn-pill ghost">Browse templates</button></Link>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#1F3A6B] to-[#F58220] p-10 md:p-14 text-white flex flex-col justify-center relative overflow-hidden">
              <img src="/brand/mascot-builder.jpg" alt="" className="absolute -right-2 -bottom-2 w-40 h-40 object-contain opacity-90 pointer-events-none mix-blend-lighten"/>
              <p className="text-white/80 uppercase text-xs tracking-wider font-black">Why teams choose CX10X</p>
              <ul className="mt-3 space-y-3 relative z-10">
                <li className="flex gap-3"><CheckCircle2 className="shrink-0" /> Self-serve onboarding — zero implementation cost</li>
                <li className="flex gap-3"><CheckCircle2 className="shrink-0" /> AI insights from day one</li>
                <li className="flex gap-3"><CheckCircle2 className="shrink-0" /> Built for global teams</li>
                <li className="flex gap-3"><CheckCircle2 className="shrink-0" /> Transparent, usage-based pricing</li>
              </ul>
            </div>
          </div>
        </Card>
      </section>
    </MarketingLayout>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <Card data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, "-")}`} className="border-0 rounded-3xl bg-white transition-all group p-6" style={{ boxShadow: "var(--shadow-soft)" }}>
      <div className="w-12 h-12 rounded-2xl bg-[#FFF0DF] text-[#F58220] flex items-center justify-center group-hover:bg-[#F58220] group-hover:text-white transition-colors">
        {icon}
      </div>
      <h3 className="mt-4 text-lg font-black text-[#1F3A6B]">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
    </Card>
  );
}

function HeroDashboardPreview() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-tr from-[#F58220]/30 to-[#1F3A6B]/20 blur-3xl rounded-3xl -z-10" />
      <Card className="border-slate-100 shadow-elev-3 overflow-hidden">
        <div className="bg-[#F58220] text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold"><LineChart size={16}/> NPS overview</div>
          <span className="text-xs bg-emerald-400/20 text-emerald-200 px-2 py-1 rounded-full">+8 this week</span>
        </div>
        <div className="p-5 grid grid-cols-3 gap-3">
          <Stat label="NPS" value="64" tone="brand"/>
          <Stat label="Promoters" value="72%" tone="green"/>
          <Stat label="Detractors" value="8%" tone="rose"/>
        </div>
        <div className="px-5 pb-5">
          <MiniSpark />
        </div>
        <div className="px-5 pb-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Top themes (AI)</p>
          <div className="flex flex-wrap gap-2">
            {[
              ["Checkout speed", "+"], ["Onboarding clarity", "+"], ["Slow support", "-"], ["Pricing", "-"], ["Mobile UX", "+"],
            ].map(([t, s]) => (
              <span key={t} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${s === "+" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-rose-50 text-rose-700 border-rose-100"}`}>{s} {t}</span>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const map = { brand: "text-[#1F3A6B]", green: "text-emerald-600", rose: "text-rose-600" };
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
      <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{label}</p>
      <p className={`mt-1 text-2xl font-extrabold ${map[tone]}`}>{value}</p>
    </div>
  );
}

function MiniSpark() {
  // Static svg sparkline
  const points = "0,38 14,32 28,30 42,22 56,28 70,16 84,14 98,8 112,16 126,10 140,4";
  return (
    <svg viewBox="0 0 140 44" className="w-full h-16">
      <defs>
        <linearGradient id="sg" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#F58220" stopOpacity="0.4"/>
          <stop offset="1" stopColor="#F58220" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polyline fill="url(#sg)" stroke="none" points={`${points} 140,44 0,44`} />
      <polyline fill="none" stroke="#F58220" strokeWidth="2" points={points} />
    </svg>
  );
}

function FakeChat() {
  return (
    <div className="space-y-3 text-sm">
      <div className="bg-white/10 rounded-xl px-4 py-3 self-end ml-auto max-w-[85%] border border-white/10">
        Why is NPS dropping in Mumbai this week?
      </div>
      <div className="bg-white text-slate-800 rounded-xl px-4 py-3 max-w-[90%] shadow">
        <p className="font-semibold text-[#1F3A6B]">Mumbai NPS dropped 12 points (Apr 21–27).</p>
        <p className="mt-1 text-slate-600">Driver: 38% of detractors mention <span className="bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded">checkout wait time</span>. Concentrated in Bandra & Andheri locations.</p>
        <p className="mt-2 text-slate-600">Recommended action: <span className="font-semibold text-[#1F3A6B]">trigger closed-loop ticket</span> for those branches and run staff coaching.</p>
      </div>
    </div>
  );
}
